import {
  type Chat,
  type ChatClientOptions,
  ChatMessages,
  timestampToDate,
  useChatStreamingClient,
} from "@storytell/cp-client";
import { batch } from "solid-js";
import type { SetStreamingStore, StreamingChatId, StreamingMessageId } from "../streamingTypes";
import { processBlockDelta, processBlockStart, processBlockStop } from "./streamingBlockHandlers";
import {
  processMessage,
  processMessageError,
  processMessageRetry,
  processMessageStart,
  processMessageStop,
} from "./streamingMessageHandlers";
import {
  processOperationComplete,
  processOperationProgress,
  processOperationStart,
} from "./streamingOperationHandlers";

/**
 * High level logic for requesting a streaming chat
 */
export const startStreamingChat = ({
  setStore,
  onChatIdCaptured,
  onMessageStarted,
  onMessageFinished,
  request,
  options,
}: {
  setStore: SetStreamingStore;
  /**
   * The chat ID is sent as part of the message start event
   * Needs a callback so the app can handle it by updating the URL
   */
  onChatIdCaptured?: (chatId: StreamingChatId) => void;
  onMessageStarted?: (messageId: StreamingMessageId) => void;
  onMessageFinished?: (messageId: StreamingMessageId) => void;
  request: Chat.StreamRequest;
  options?: ChatClientOptions;
}) => {
  const chatClient = useChatStreamingClient();
  const stream = chatClient.stream(request, options);

  processStreamingChat(setStore, stream, onChatIdCaptured, onMessageStarted, onMessageFinished);
};

/**
 * Process a streaming async chat
 * @param setStore - The setStore function to update the store
 * @param stream - The stream of chat messages
 * @returns void
 */
export const processStreamingChat = async (
  setStore: SetStreamingStore,
  stream: AsyncIterable<Chat.StreamResponse> | AsyncIterable<Chat.StreamPlaybackResponse>,
  onChatIdCaptured?: (chatId: StreamingChatId) => void,
  onMessageStarted?: (messageId: StreamingMessageId) => void,
  onMessageFinished?: (messageId: StreamingMessageId) => void,
) => {
  for await (const response of stream) {
    // Ensure no rendering/side-effecting happens until we finish all processing is done
    batch(() => {
      for (const message of response.messages) {
        processResponseMessage(setStore, message, onChatIdCaptured, onMessageStarted, onMessageFinished, undefined);
      }
    });
  }
};

/**
 * Process a completed chat (synchronous)
 * @param setStore - The setStore function to update the store
 * @param stream - The stream of chat messages
 * @returns void
 */
export const processCompletedChat = (
  setStore: SetStreamingStore,
  stream: Iterable<Chat.StreamResponse | Chat.StreamPlaybackResponse>,
  onChatIdCaptured?: (chatId: StreamingChatId) => void,
) => {
  batch(() => {
    for (const response of stream) {
      for (const message of response.messages) {
        processResponseMessage(setStore, message, onChatIdCaptured, undefined, undefined, undefined);
      }
    }
  });
};

export const processChatRecording = (setStore: SetStreamingStore, recording: Chat.ConversationResponse) => {
  const chatId = recording.chatId;
  const recordings = recording.messages;
  // TODO: @andi streaming - Handle messages not being fully loaded (partial chat loading)
  batch(() => {
    for (const recording of recordings) {
      for (const message of recording.messages) {
        // Extract timestamp from recording event if available
        const eventTimestamp = message.timestamp ? timestampToDate(message.timestamp) : undefined;
        processResponseMessage(setStore, message.message, undefined, undefined, undefined, eventTimestamp);
      }
    }
  });
};

export const processResponseMessage = (
  setStore: SetStreamingStore,
  message: ChatMessages.Message,
  onChatIdCaptured?: (chatId: StreamingChatId) => void,
  onMessageStarted?: (messageId: StreamingMessageId) => void,
  onMessageFinished?: (messageId: StreamingMessageId) => void,
  eventTimestamp?: Date,
) => {
  // For debugging, it maps the number enum to the name of the event
  // console.log(Object.entries(ChatMessages.Event).find(([key, value]) => value === message.event)?.[0]);
  switch (message.event) {
    case ChatMessages.Event.MESSAGE_ERROR:
      processMessageError(setStore, message);
      return;
    case ChatMessages.Event.MESSAGE_RETRY:
      processMessageRetry(setStore, message);
      return;
    case ChatMessages.Event.MESSAGE:
      processMessage(setStore, message);
      return;
    case ChatMessages.Event.MESSAGE_START:
      processMessageStart(setStore, message, onChatIdCaptured, onMessageStarted);
      return;
    case ChatMessages.Event.MESSAGE_STOP:
      processMessageStop(setStore, message);
      onMessageFinished?.(message.id as StreamingMessageId);
      return;
    case ChatMessages.Event.OPERATION_START:
      processOperationStart(setStore, message);
      return;
    case ChatMessages.Event.OPERATION_PROGRESS:
      processOperationProgress(setStore, message);
      return;
    case ChatMessages.Event.OPERATION_COMPLETE:
      processOperationComplete(setStore, message);
      return;
    case ChatMessages.Event.MESSAGE_BLOCK_START:
    case ChatMessages.Event.MESSAGE_BLOCK_GENERATION_START:
      processBlockStart(setStore, message, eventTimestamp);
      return;
    case ChatMessages.Event.MESSAGE_DELTA:
    case ChatMessages.Event.MESSAGE_BLOCK_DELTA:
    case ChatMessages.Event.MESSAGE_BLOCK_GENERATION_DELTA:
      processBlockDelta(setStore, message);
      return;
    case ChatMessages.Event.MESSAGE_BLOCK_STOP:
    case ChatMessages.Event.MESSAGE_BLOCK_GENERATION_STOP:
      processBlockStop(setStore, message, eventTimestamp);
      return;

    // TODO: @andi streaming - add these back in
    // case ChatMessages.Event.OPERATION_FAILED:
    //   handlers.onOperationFailed(message);
    //   break;

    // case ChatMessages.Event.OPERATION_CANCELLED:
    //   handlers.onOperationCancelled(message);
    //   break;
  }
};
